/**
 * The "Attachments" section a ticket page and a customer page share: the documents uploaded onto
 * that record, with Upload, View, Download and Delete.
 *
 *   * VIEW AND DOWNLOAD ARE DECIDED BY THE SERVER (`canView`): plain text and PDF open in a tab,
 *     everything else is saved. The tab is opened inside the click, before any await, or the browser
 *     blocks it as a pop-up - see `openInNewTab`.
 *   * THE FILE PICKER RUNS INSIDE THE CLICK TOO, for the same reason: a picker opened after an await
 *     has lost the click's activation.
 *   * DELETE IS THE UPLOADER'S OR AN ADMINISTRATOR'S, server-enforced; the button is hidden for
 *     everyone else rather than shown to answer 403.
 */
import * as auth from '../../api/auth.js';
import * as crm from '../../api/crm.js';
import { describeError } from '../../api/http.js';
import type { AttachmentDto } from '../../contracts.gen.js';
import { download, openInNewTab, pickFiles, uploadFiles } from '../../download.js';
import { bytes, join, localDateTime, plural } from '../../format.js';
import { Card } from '../../ui/Card.js';
import { CardList } from '../../ui/CardList.js';
import { confirm } from '../../ui/dialogs.js';
import { EmptyState } from '../../ui/EmptyState.js';
import { StatusLine } from '../../ui/StatusLine.js';
import { button, buttonRow, card, fillWith, FONT, GAP, h2, muted, relayout, splitRow, stackOf, value, vstack } from '../../ui/layout.js';
import type { PageContext } from '../page.js';
import { insets } from 'webwin';

export interface AttachmentsSection {
  readonly panel: import('webwin').StackPanel;
  refresh(): Promise<void>;
}

export function attachmentsSection(options: {
  ctx: PageContext;
  /** One of the two: the files on a ticket, or the files on the customer itself. */
  owner: { customerId?: number; ticketId?: number };
}): AttachmentsSection {
  const { ctx, owner } = options;
  let busy = false;

  const status = new StatusLine();
  const empty = new EmptyState(
    owner.ticketId !== undefined
      ? 'No files on this ticket yet. Upload a log, a screenshot or a quote.'
      : 'No documents for this customer yet. Upload a contract, a network diagram or a price list.'
  );
  const list = new CardList<AttachmentDto>({
    key: a => a.id,
    version: a => `${a.fileName}|${a.sizeBytes}`,
    build: buildCard,
    spacing: GAP.sm
  });

  const uploadButton = button('Upload files', () => void upload(), true);
  const header = splitRow({ align: 'center' });
  fillWith(header, h2('Attachments'));
  header.controls.add(uploadButton);

  const panel = stackOf(vstack({ spacing: GAP.sm }), header, status.label, empty.panel, list.panel);

  function buildCard(a: AttachmentDto): Card {
    const me = auth.currentUser();
    const canDelete = auth.isAdmin() || (me !== null && me.id === a.uploadedByUserId);

    const actions = buttonRow({ spacing: GAP.sm });
    if (a.canView) actions.controls.add(button('View', () => void view(a)));
    actions.controls.add(button('Download', () => void save(a)));
    if (canDelete) actions.controls.add(button('Delete', () => void remove(a)));

    const body = stackOf(
      vstack({ spacing: GAP.xs }),
      value(a.fileName, { font: FONT.bold }),
      muted(join([bytes(a.sizeBytes), a.uploadedByName, localDateTime(a.uploadedUtc)])),
      actions
    );
    const shell = card({ padding: insets(12) });
    shell.controls.add(body);
    return shell;
  }

  function setBusy(next: boolean): void {
    busy = next;
    uploadButton.enabled = !next;
  }

  async function refresh(): Promise<void> {
    try {
      const rows = await crm.getAttachments(owner, ctx.dispose.signal());
      if (ctx.cancelled()) return;
      list.set(rows);
      empty.update(rows.length);
      relayout(panel);
    } catch (error) {
      if (!ctx.cancelled()) status.error('Could not load the attachments: ' + describeError(error));
    }
  }

  async function upload(): Promise<void> {
    if (busy) return;
    // Picked before any await: the dialog needs the click's activation.
    const picked = pickFiles(true);
    const files = await picked;
    if (files.length === 0 || ctx.cancelled()) return;
    setBusy(true);
    const what = files.length === 1 ? files[0]!.name : plural(files.length, 'file');
    status.busy(`Uploading ${what}…`);
    try {
      await uploadFiles(crm.attachmentUploadPath(owner), files, fraction => {
        if (!ctx.cancelled()) status.busy(`Uploading ${what}… ${Math.round(fraction * 100)}%`);
      });
      if (ctx.cancelled()) return;
      status.ok(files.length === 1 ? `${what} uploaded.` : `${what} uploaded.`);
      await refresh();
    } catch (error) {
      if (!ctx.cancelled()) status.error('Could not upload: ' + describeError(error));
    } finally {
      if (!ctx.cancelled()) setBusy(false);
    }
  }

  /** Opens the tab synchronously, so this must stay the click handler itself. */
  function view(a: AttachmentDto): Promise<void> {
    status.clear();
    return openInNewTab(crm.attachmentPath(a.id, true), a.fileName).catch(error => {
      if (!ctx.cancelled()) status.error(`Could not open ${a.fileName}: ` + describeError(error));
    });
  }

  async function save(a: AttachmentDto): Promise<void> {
    status.busy(`Downloading ${a.fileName}…`);
    try {
      await download(crm.attachmentPath(a.id), a.fileName);
      if (!ctx.cancelled()) status.clear();
    } catch (error) {
      if (!ctx.cancelled()) status.error(`Could not download ${a.fileName}: ` + describeError(error));
    }
  }

  async function remove(a: AttachmentDto): Promise<void> {
    if (busy) return;
    const yes = await confirm(ctx.desktop, {
      title: 'Delete attachment',
      message: `Delete ${a.fileName}? It is removed from the CRM; anyone who downloaded it keeps their copy.`
    });
    if (!yes || ctx.cancelled()) return;
    setBusy(true);
    status.busy(`Deleting ${a.fileName}…`);
    try {
      await crm.deleteAttachment(a.id);
      if (ctx.cancelled()) return;
      status.ok(`${a.fileName} deleted.`);
      await refresh();
    } catch (error) {
      if (!ctx.cancelled()) status.error(`Could not delete ${a.fileName}: ` + describeError(error));
    } finally {
      if (!ctx.cancelled()) setBusy(false);
    }
  }

  return { panel, refresh };
}
